import { updateInvoiceStatusMutation } from "@/admin/reports/reportsQuery.ts";
import type { GQInvoiceItem } from "@/admin/reports/ReportsTable.tsx";
import { WModalButton, WTooltipButton } from "@/components/button/WToolButton.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { ReportStatus } from "@/modules/billing/ReportStatus.tsx";
import { InvoiceStatusEnum } from "@warrenio/api-spec/spec/billingEnums";
import { notNull } from "@warrenio/utils/notNull";

interface UpdateInvoiceStatusModalProps {
    item: GQInvoiceItem;
    inTable?: boolean;
}

function UpdateInvoiceStatusModalContent({ item }: UpdateInvoiceStatusModalProps) {
    const updateMutation = useStandardMutation(updateInvoiceStatusMutation);

    const { document_number, status, billing_account } = item;

    async function onSubmit() {
        await updateMutation.mutateAsync({
            document_number: notNull(document_number),
            status: status === InvoiceStatusEnum.PAID ? "unpaid" : "paid",
        });
    }

    return (
        <WModalContent
            title={status === InvoiceStatusEnum.PAID ? "Mark as unpaid" : "Mark as paid"}
            label="Update"
            autoFocus
            modalAction={async () => await onSubmit()}
        >
            <DetailsTable>
                <DetailsTableRow title="Document No.">{document_number}</DetailsTableRow>

                <DetailsTableRow title="New status">
                    <ReportStatus value={status === 5 ? 10 : 5} />
                </DetailsTableRow>

                <DetailsTableRow title="Billing account">{billing_account?.title}</DetailsTableRow>
            </DetailsTable>
        </WModalContent>
    );
}

export function UpdateInvoiceStatusBlock(props: UpdateInvoiceStatusModalProps) {
    const { item, inTable } = props;

    const button = inTable ? (
        <WTooltipButton
            title={item.status === InvoiceStatusEnum.PAID ? "Mark as unpaid" : "Mark as paid"}
            variant="ghost"
            size="xs"
            icon="i-lucide:hand-coins"
            color="primary"
            action={undefined}
        />
    ) : (
        <WModalButton
            label={item.status === InvoiceStatusEnum.PAID ? "Mark as unpaid" : "Mark as paid"}
            size="md"
            icon="i-lucide:hand-coins"
        />
    );

    return (
        item.type === "cloud_services" &&
        item.billing_account?.allow_debt && (
            <WModal button={button}>
                <UpdateInvoiceStatusModalContent {...props} />
            </WModal>
        )
    );
}
